'use client';

import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import { I18nextProvider } from 'react-i18next';
import i18n from '@/i18n/config';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
import 'dayjs/locale/en';
import 'dayjs/locale/ja';
import { useGlobalConfig } from '@/contexts/global-config-context';
import { authTokenManager } from '@/lib/deep-mining/auth-token-manager';

interface LanguageContextType {
  currentLanguage: string;
  changeLanguage: (lng: string) => Promise<void>;
}

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined,
);

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a I18nProvider');
  }
  return context;
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const { globalConfig, isLoading } = useGlobalConfig();
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language);

  useEffect(() => {
    // 设置初始语言
    const setDayjsLocale = (lng: string) => {
      // 将 i18n 语言代码映射到 dayjs locale
      const dayjsLocale =
        lng === 'zh-CN' ? 'zh-cn' : lng === 'ja' ? 'ja' : 'en';
      dayjs.locale(dayjsLocale);
    };

    // 检查是否为第三方接入页面
    if (typeof window !== 'undefined' && window.DM_IS_THIRD_PARTY) {
      // 如果是第三方接入，强制设置语言为中文
      i18n.changeLanguage('zh-CN');
      document.documentElement.lang = 'zh-CN';
      setCurrentLanguage('zh-CN');
      return;
    }

    // 如果未登录且全局配置已加载，使用系统语言
    if (!isLoading && !authTokenManager.isTokenExist) {
      const systemLanguage = globalConfig['system_language']?.value;
      if (systemLanguage && i18n.language !== systemLanguage) {
        i18n.changeLanguage(systemLanguage);
        document.documentElement.lang = systemLanguage;
        setDayjsLocale(systemLanguage);
        setCurrentLanguage(systemLanguage);
      }
    }

    // 挂载时设置 HTML lang 属性和 dayjs locale
    document.documentElement.lang = i18n.language;
    setDayjsLocale(i18n.language);

    // 监听语言变化
    const handleLanguageChange = (lng: string) => {
      document.documentElement.lang = lng;
      setDayjsLocale(lng);
      setCurrentLanguage(lng);
    };

    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [globalConfig, isLoading]);

  const changeLanguage = async (lng: string) => {
    await i18n.changeLanguage(lng);
    // setCurrentLanguage 会通过监听器自动更新
  };

  return (
    <I18nextProvider i18n={i18n}>
      <LanguageContext.Provider value={{ currentLanguage, changeLanguage }}>
        {children}
      </LanguageContext.Provider>
    </I18nextProvider>
  );
}
